import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AppShell } from '../components/AppShell';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../components/ui/sheet';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  AddressBook, Plus, MagnifyingGlass, PencilSimple, Trash,
  Phone, Envelope, User, Briefcase, NoteBlank, Buildings
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { CORPS_ETAT_CATALOG, getCorpsEtatLabel } from '../constants/corpsEtat';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function AnnuairePage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nom: '', representant: '', corps_etat: '', sous_corps_etat: '', email: '', telephone: '', note: '' });

  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/annuaire`, { withCredentials: true });
      setEntries(res.data || []);
    } catch (_err) { setEntries([]); }
    finally { setLoading(false); }
  }, [setEntries, setLoading]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const resetForm = () => setForm({ nom: '', representant: '', corps_etat: '', sous_corps_etat: '', email: '', telephone: '', note: '' });

  const openCreate = () => {
    resetForm();
    setEditing(null);
    setSheetOpen(true);
  };

  const openEdit = (entry) => {
    setForm({
      nom: entry.nom || '', representant: entry.representant || '',
      corps_etat: entry.corps_etat || '',
      sous_corps_etat: entry.sous_corps_etat || '',
      email: entry.email || '',
      telephone: entry.telephone || '', note: entry.note || '',
    });
    setEditing(entry);
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.nom.trim()) return toast.error('Le nom est obligatoire');
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/annuaire/${editing.id}`, form, { withCredentials: true });
        toast.success('Entreprise mise à jour');
      } else {
        await axios.post(`${API}/annuaire`, form, { withCredentials: true });
        toast.success('Entreprise ajoutée à l\'annuaire');
      }
      setSheetOpen(false);
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/annuaire/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Entreprise supprimée');
      setDeleteTarget(null);
      fetchEntries();
    } catch (_err) { toast.error('Erreur lors de la suppression'); }
  };

  const filtered = entries.filter(e =>
    !search || e.nom?.toLowerCase().includes(search.toLowerCase()) ||
    e.representant?.toLowerCase().includes(search.toLowerCase()) ||
    e.corps_etat?.toLowerCase().includes(search.toLowerCase()) ||
    e.sous_corps_etat?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <AppShell>
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#0055FF]" />
      </div>
    </AppShell>
  );

  return (
    <AppShell>
      <div className="space-y-5 max-w-4xl mx-auto" data-testid="annuaire-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#111827] flex items-center gap-2" data-testid="annuaire-title">
              <AddressBook size={28} className="text-[#0055FF]" weight="fill" />
              Annuaire entreprises
            </h1>
            <p className="text-sm text-[#6B7280] mt-0.5">
              {entries.length} entreprise{entries.length !== 1 ? 's' : ''} enregistree{entries.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button onClick={openCreate} className="bg-[#0055FF] hover:bg-blue-700 h-10 px-4" data-testid="annuaire-add-btn">
            <Plus size={18} className="mr-1.5" /> Ajouter
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher une entreprise..."
            className="pl-10 h-10"
            data-testid="annuaire-search"
          />
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <Card className="border-dashed border-2 border-[#D1D5DB]">
            <CardContent className="p-8 text-center">
              <AddressBook size={40} className="mx-auto text-[#9CA3AF] mb-3" />
              <p className="text-sm text-[#6B7280]">
                {search ? 'Aucune entreprise trouvee' : 'Votre annuaire est vide'}
              </p>
              {!search && (
                <p className="text-xs text-[#9CA3AF] mt-1">
                  Ajoutez des entreprises ici ou elles seront ajoutées automatiquement depuis vos chantiers.
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map(entry => (
              <Card key={entry.id} className="border hover:shadow-sm transition-shadow" data-testid={`annuaire-entry-${entry.id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#111827] text-sm" data-testid={`annuaire-nom-${entry.id}`}>
                          {entry.nom}
                        </span>
                        {entry.corps_etat && (
                          <span className="text-xs bg-[#EEF2FF] text-[#4338CA] px-2 py-0.5 rounded-full font-medium">
                            {getCorpsEtatLabel(entry.corps_etat)}{entry.sous_corps_etat ? ` — ${entry.sous_corps_etat}` : ''}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                        {entry.representant && (
                          <span className="text-xs text-[#6B7280] flex items-center gap-1">
                            <User size={12} /> {entry.representant}
                          </span>
                        )}
                        {entry.telephone && (
                          <span className="text-xs text-[#6B7280] flex items-center gap-1">
                            <Phone size={12} /> {entry.telephone}
                          </span>
                        )}
                        {entry.email && (
                          <span className="text-xs text-[#6B7280] flex items-center gap-1">
                            <Envelope size={12} /> {entry.email}
                          </span>
                        )}
                      </div>
                      {entry.note && (
                        <p className="text-xs text-[#9CA3AF] mt-1 italic">
                          {entry.note}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-[#6B7280] hover:text-[#0055FF]"
                        onClick={() => openEdit(entry)} data-testid={`annuaire-edit-${entry.id}`}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-[#6B7280] hover:text-red-500"
                        onClick={() => setDeleteTarget(entry)} data-testid={`annuaire-delete-${entry.id}`}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="annuaire-sheet">
          <SheetHeader>
            <SheetTitle>{editing ? 'Modifiér l\'entreprise' : 'Nouvelle entreprise'}</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div>
              <label className="text-sm font-medium text-[#374151] mb-1 block">Nom *</label>
              <div className="relative">
                <Buildings size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <Input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })}
                  placeholder="Nom de l'entreprise" className="pl-9" data-testid="annuaire-form-nom" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#374151] mb-1 block">Representant</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <Input value={form.representant} onChange={e => setForm({ ...form, representant: e.target.value })}
                  placeholder="Nom du representant" className="pl-9" data-testid="annuaire-form-representant" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#374151] mb-1 block">Corps d&apos;état / Rôle</label>
              <Select value={form.corps_etat || '_none'} onValueChange={(v) => setForm({ ...form, corps_etat: v === '_none' ? '' : v })}>
                <SelectTrigger className="h-10" data-testid="annuaire-form-corps-select">
                  <div className="flex items-center gap-2">
                    <Briefcase size={16} className="text-[#9CA3AF]" />
                    <SelectValue placeholder="Sélectionner un corps d'état ou un rôle" />
                  </div>
                </SelectTrigger>
                <SelectContent position="popper" className="z-[100] max-h-80">
                  <SelectItem value="_none" className="text-[#6B7280]">— Aucun —</SelectItem>
                  {CORPS_ETAT_CATALOG.map(g => (
                    <SelectGroup key={g.group}>
                      <SelectLabel className="text-[10px] uppercase tracking-widest text-[#0055FF]">{g.group}</SelectLabel>
                      {g.items.map(it => (
                        <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#374151] mb-1 block">Précision / sous-corps d&apos;état (optionnel)</label>
              <Input value={form.sous_corps_etat} onChange={e => setForm({ ...form, sous_corps_etat: e.target.value })}
                placeholder="Ex: bardage bois, MT/BT, VMC double flux…"
                data-testid="annuaire-form-sous-corps" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#374151] mb-1 block">Email</label>
              <div className="relative">
                <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="contact@entreprise.fr" className="pl-9" type="email" data-testid="annuaire-form-email" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#374151] mb-1 block">Telephone</label>
              <div className="relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <Input value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })}
                  placeholder="06 12 34 56 78" className="pl-9" data-testid="annuaire-form-telephone" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#374151] mb-1 block">Note interne</label>
              <Textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
                placeholder="Ex: Fiable, bon contact, retard frequent..." rows={2}
                data-testid="annuaire-form-note" />
            </div>
            <Button onClick={handleSave} disabled={saving || !form.nom.trim()}
              className="w-full h-11 bg-[#0055FF] hover:bg-blue-700 font-medium" data-testid="annuaire-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editing ? 'Mettre à jour' : 'Ajouter à l\'annuaire'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="annuaire-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimér {deleteTarget?.nom} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette entreprise sera supprimée de votre annuaire. Les entreprises déjà ajoutées à vos chantiers ne seront pas affectées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="annuaire-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700" data-testid="annuaire-delete-confirm">
              Supprimér
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
